import axios from 'axios'
import { API_BASE_URL_WITH_VERSION } from '../config/api'
import { config } from '../config/environment'

// API Configuration
const api = axios.create({
  baseURL: API_BASE_URL_WITH_VERSION,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Types
export interface BirthData {
  name: string
  day: number
  month: number
  year: number
  hour: number
  minute: number
  latitude: number
  longitude: number
  timezone: number
  place_of_birth?: string
}

export interface AstroReading {
  type: string
  data: any
  timestamp: string
  user_id: string
}

// API Functions
export const waitlistAPI = {
  // Join waitlist
  joinWaitlist: async (data: { name: string; email: string; phone?: string; interest: string; source?: string }) => {
    try {
      const response = await api.post('/waitlist/join', data);
      return response.data;
    } catch (error: any) {
      console.error('Error joining waitlist:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to join waitlist' 
      };
    }
  },

  // Get waitlist stats (admin only)
  getWaitlistStats: async () => {
    try {
      const response = await api.get('/waitlist/stats');
      return response.data;
    } catch (error: any) {
      console.error('Error getting waitlist stats:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to get waitlist stats' 
      };
    }
  }
};

export const astrologyAPI = {
  // Collect birth details
  collectBirthDetails: async (userId: string, birthData: BirthData) => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/astrology/comprehensive-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          birthData
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error collecting birth details:', error)
      return { success: false, error: 'Failed to collect birth details' }
    }
  },

  // Get personalized reading
  getPersonalizedReading: async (userId: string, readingType: string, birthData: BirthData) => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/chat/get-reading`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          readingType,
          birthData
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error getting reading:', error)
      return { success: false, error: 'Failed to get reading' }
    }
  },

  // Get ML insights
  getMLInsights: async (userId: string, query: string) => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/langchain/analyze_birth_chart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          birth_data: { name: 'User' },
          user_query: query
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error getting ML insights:', error)
      return { success: false, error: 'Failed to get ML insights' }
    }
  },

  // Get comprehensive analysis
  getComprehensiveAnalysis: async (userId: string, birthData: BirthData) => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/astrology/comprehensive-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          birthData
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error getting comprehensive analysis:', error)
      return { success: false, error: 'Failed to get comprehensive analysis' }
    }
  },

  // Analyze compatibility between two people
  analyzeCompatibility: async (userId: string, person1Data: BirthData, person2Data: BirthData) => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/langchain/compatibility`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          person1_data: person1Data,
          person2_data: person2Data
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      return {
        success: true,
        compatibility: data.compatibility,
        person1: data.person1,
        person2: data.person2,
        user_id: userId,
        timestamp: new Date().toISOString()
      }
    } catch (error) {
      console.error('Error analyzing compatibility:', error)
      return { success: false, error: 'Failed to analyze compatibility' }
    }
  },
}

export const masterOrchestratorAPI = {
  // Process query through master orchestrator
  processQuery: async (userId: string, query: string) => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
          userContext: { userId }
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      return {
        success: true,
        response: data.response || data.answer || 'Response generated successfully!'
      }
    } catch (error) {
      console.error('Error processing query:', error)
      return { success: false, error: 'Failed to process query' }
    }
  },

  // Set user birth data
  setBirthData: async (userId: string, birthData: BirthData) => {
    try {
      const response = await fetch('${config.apiBaseUrl}/api/langchain/analyze_birth_chart', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          birth_data: birthData
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      return {
        success: true,
        user_id: userId,
        birth_data: birthData,
        analysis: data.analysis
      }
    } catch (error) {
      console.error('Error setting birth data:', error)
      return { success: false, error: 'Failed to set birth data' }
    }
  },

  // Get user context
  getUserContext: async (userId: string) => {
    try {
      const response = await fetch('${config.apiBaseUrl}/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: 'Get my user context and preferences',
          userContext: { userId }
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      return {
        success: true,
        user_context: data.user_context || {},
        preferences: data.preferences || {}
      }
    } catch (error) {
      console.error('Error getting user context:', error)
      return { success: false, error: 'Failed to get user context' }
    }
  },

  // Enhanced Astrology API - Get personalized reading
  getPersonalizedReading: async (userId: string, readingType: string, birthData: BirthData) => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `Please provide a detailed ${readingType} reading for me based on my birth data.`,
          userContext: { userId },
          birthData,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { reading: data.response };
    } catch (error) {
      console.error('Error getting enhanced reading:', error);
      return { error: 'Failed to get enhanced reading' };
    }
  },

  // Enhanced Astrology API - Send chat message
  sendChatMessage: async (userId: string, message: string, birthData: BirthData) => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: message,
          userContext: { userId },
          birthData,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { response: data.response };
    } catch (error) {
      console.error('Error sending chat message:', error);
      return { error: 'Failed to send message' };
    }
  },
}

// LangChain API for intelligent chat responses
export const langChainAPI = {
  // Process chat queries with LangChain agent
  processChatQuery: async (query: string, userContext?: any, conversationHistory?: any[]) => {
    try {
      const response = await fetch('${config.apiBaseUrl}/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
          userContext,
          conversationHistory: conversationHistory || []
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      return await response.json()
    } catch (error) {
      console.error('Error processing chat query:', error)
      return { success: false, error: 'Failed to process query' }
    }
  },

  // Analyze birth chart with LangChain
  analyzeBirthChart: async (birthData: BirthData) => {
    try {
      // Format birth data for backend
      const formattedBirthData = {
        name: birthData.name,
        birth_date: `${birthData.year}-${String(birthData.month).padStart(2, '0')}-${String(birthData.day).padStart(2, '0')}`,
        birth_time: `${String(birthData.hour).padStart(2, '0')}:${String(birthData.minute).padStart(2, '0')}`,
        birth_location: birthData.place_of_birth || 'Unknown',
        latitude: birthData.latitude,
        longitude: birthData.longitude
      }

      const response = await fetch('${config.apiBaseUrl}/api/langchain/analyze_birth_chart', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          birth_data: formattedBirthData
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      return await response.json()
    } catch (error) {
      console.error('Error analyzing birth chart:', error)
      return { success: false, error: 'Failed to analyze birth chart' }
    }
  },

  // Get personalized reading with LangChain
  getPersonalizedReading: async (readingType: string, birthData: BirthData) => {
    try {
      // Format birth data for backend
      const formattedBirthData = {
        name: birthData.name,
        birth_date: `${birthData.year}-${String(birthData.month).padStart(2, '0')}-${String(birthData.day).padStart(2, '0')}`,
        birth_time: `${String(birthData.hour).padStart(2, '0')}:${String(birthData.minute).padStart(2, '0')}`,
        birth_location: birthData.place_of_birth || 'Unknown',
        latitude: birthData.latitude,
        longitude: birthData.longitude
      }

      const response = await fetch('${config.apiBaseUrl}/api/langchain/get_reading', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reading_type: readingType,
          birth_data: formattedBirthData
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      return await response.json()
    } catch (error) {
      console.error('Error getting reading:', error)
      return { success: false, error: 'Failed to get reading' }
    }
  },

  // Get current transits
  getCurrentTransits: async (birthData: BirthData) => {
    try {
      // Format birth data for backend
      const formattedBirthData = {
        name: birthData.name,
        birth_date: `${birthData.year}-${String(birthData.month).padStart(2, '0')}-${String(birthData.day).padStart(2, '0')}`,
        birth_time: `${String(birthData.hour).padStart(2, '0')}:${String(birthData.minute).padStart(2, '0')}`,
        birth_location: birthData.place_of_birth || 'Unknown',
        latitude: birthData.latitude,
        longitude: birthData.longitude
      }

      const response = await fetch('${config.apiBaseUrl}/api/langchain/current_transits', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          birth_data: formattedBirthData
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      return await response.json()
    } catch (error) {
      console.error('Error getting current transits:', error)
      return { success: false, error: 'Failed to get current transits' }
    }
  },
}

export const wellnessAPI = {
  // Dosha analysis
  getDoshaAnalysis: async (userId: string, answers: number[], query?: string) => {
    try {
      const response = await fetch('${config.apiBaseUrl}/api/wellness/dosha-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          answers,
          query
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      return await response.json()
    } catch (error) {
      console.error('Error getting dosha analysis:', error)
      return { success: false, error: 'Failed to get dosha analysis' }
    }
  },

  // Yoga recommendations
  getYogaRecommendations: async (userId: string, query: string) => {
    try {
      const response = await fetch('${config.apiBaseUrl}/api/wellness/yoga-recommendations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          query
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      return await response.json()
    } catch (error) {
      console.error('Error getting yoga recommendations:', error)
      return { success: false, error: 'Failed to get yoga recommendations' }
    }
  },

  // Mantra suggestions
  getMantraSuggestions: async (userId: string, query: string) => {
    try {
      const response = await fetch('${config.apiBaseUrl}/api/wellness/mantra-suggestions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          query
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      return await response.json()
    } catch (error) {
      console.error('Error getting mantra suggestions:', error)
      return { success: false, error: 'Failed to get mantra suggestions' }
    }
  },

  // Ritual recommendations
  getRitualRecommendations: async (userId: string, query: string) => {
    try {
      const response = await fetch('${config.apiBaseUrl}/api/wellness/ritual-recommendations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          query
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      return await response.json()
    } catch (error) {
      console.error('Error getting ritual recommendations:', error)
      return { success: false, error: 'Failed to get ritual recommendations' }
    }
  },

  // Wellness advice
  getWellnessAdvice: async (userId: string, query: string) => {
    try {
      const response = await fetch('${config.apiBaseUrl}/api/wellness/wellness-advice', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          query
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      return await response.json()
    } catch (error) {
      console.error('Error getting wellness advice:', error)
      return { success: false, error: 'Failed to get wellness advice' }
    }
  },

  // Cosmic guidance
  getCosmicGuidance: async (userId: string, query: string) => {
    try {
      const response = await fetch('${config.apiBaseUrl}/api/wellness/cosmic-guidance', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          query
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      return await response.json()
    } catch (error) {
      console.error('Error getting cosmic guidance:', error)
      return { success: false, error: 'Failed to get cosmic guidance' }
    }
  },
}

export const compatibilityAPI = {
  analyzeCompatibility: async (
    userBirthData: any,
    contactBirthData: any
  ) => {
    try {
      const response = await fetch('${config.apiBaseUrl}/api/langchain/compatibility', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          person1_data: userBirthData,
          person2_data: contactBirthData
        }),
      })

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Compatibility analysis error:', error)
      return {
        success: false,
        error: 'Failed to analyze compatibility'
      }
    }
  }
}

export const matchmakingAPI = {
  getMatchmakingReport: async (maleData: any, femaleData: any) => {
    try {
      const response = await fetch('${config.apiBaseUrl}/api/user-journey/matchmaking', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          maleData,
          femaleData
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error getting matchmaking report:', error)
      return { success: false, error: 'Failed to get matchmaking report' }
    }
  }
}

// Health check
export const healthCheck = async () => {
  const response = await api.get('/health')
  return response.data
}

export default api 

// Yoga Guru API for enhanced yoga recommendations
export const yogaGuruAPI = {
  // Get personalized yoga recommendations
  getYogaRecommendations: async (query: string, userContext?: any) => {
    try {
      const response = await fetch('${config.apiBaseUrl}/api/wellness/yoga-recommendations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
          userContext
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      return await response.json()
    } catch (error) {
      console.error('Error getting yoga recommendations:', error)
      return { success: false, error: 'Failed to get yoga recommendations' }
    }
  },

  // Get all available yoga poses
  getAllPoses: async () => {
    try {
      const response = await fetch('${config.apiBaseUrl}/api/wellness/yoga-poses', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      return await response.json()
    } catch (error) {
      console.error('Error getting yoga poses:', error)
      return { success: false, error: 'Failed to get yoga poses' }
    }
  }
} 

// Astrology Chart Import API
export const astrologyChartAPI = {
  // Import charts automatically when user has complete birth data
  importCharts: async (userId: string, birthData: any) => {
    try {
      const response = await fetch(`${api.defaults.baseURL}/api/astrology/import-charts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId, birthData }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error importing charts:', error);
      throw error;
    }
  },

  // Get user's imported charts
  getUserCharts: async (userId: string) => {
    try {
      const response = await fetch(`${api.defaults.baseURL}/api/astrology/user-charts/${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting user charts:', error);
      throw error;
    }
  },

  // Get comprehensive astrological analysis
  getComprehensiveAnalysis: async (userId: string, birthData: any) => {
    try {
      const response = await fetch(`${api.defaults.baseURL}/api/astrology/comprehensive-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId, birthData }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting comprehensive analysis:', error);
      throw error;
    }
  },

  // Import contact charts
  importContactCharts: async (userId: string, contactId: string, contactName: string, birthData: any) => {
    try {
      const requestData = { userId, contactId, contactName, birthData };
      console.log('Sending contact chart import request:', requestData);
      
      const response = await fetch(`${api.defaults.baseURL}/api/astrology/import-contact-charts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Contact chart import failed:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error importing contact charts:', error);
      throw error;
    }
  },

  // Get contact's imported charts
  getContactCharts: async (contactId: string) => {
    try {
      const response = await fetch(`${api.defaults.baseURL}/api/astrology/contact-charts/${contactId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting contact charts:', error);
      throw error;
    }
  },

  // Get chart image
  getChartImage: async (chartId: string) => {
    try {
      const response = await fetch(`${api.defaults.baseURL}/api/astrology/chart-image/${chartId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting chart image:', error);
      throw error;
    }
  }
}; 